/**
 * Which character image a runner shows and how code moves it (docs/art/ART_DIRECTION.md §2:
 * characters are single poses, animated with squash, stretch, bob and tilt). Frame strips still
 * work; they animate themselves and get no extra motion.
 */

/** Runner poses; `lose` has no image of its own in the art contract and falls back to `idle`. */
export type RunnerAnim =
  | 'idle'
  | 'run'
  | 'jump'
  | 'flip'
  | 'fall'
  | 'hit'
  | 'win'
  | 'lose'
  // Asked for by screens, not picked from the runner state (PHASE3 T3.5, T3.11):
  | 'cheer'
  | 'slump'
  | 'sit'
  | 'burrow'

/** The runner fields the pose is picked from. */
export interface RunnerLook {
  vx: number
  vy: number
  grounded: boolean
  stunned: boolean
  placement: number
}

export function runnerAnim(p: RunnerLook): RunnerAnim {
  if (p.stunned) return 'hit'
  if (p.placement > 0 && p.grounded && p.vx === 0) return p.placement === 1 ? 'win' : 'lose'
  if (!p.grounded) return p.vy < 0 ? 'jump' : 'fall'
  return p.vx === 0 ? 'idle' : 'run'
}

/** How long the double-jump somersault lasts. */
export const FLIP_MS = 420

/**
 * A double jump: an air jump was spent since the last frame while airborne (D-044: the first
 * jump off the ground does not use one; landing gives it back).
 */
export function isDoubleJump(
  prev: { grounded: boolean; jumpsLeft: number },
  now: { grounded: boolean; jumpsLeft: number },
): boolean {
  return !now.grounded && now.jumpsLeft < prev.jumpsLeft
}

/** Images to try for a pose, best first, when some are not delivered yet. */
const FALLBACKS: Record<RunnerAnim, readonly string[]> = {
  idle: ['idle', 'run'],
  run: ['run', 'idle'],
  jump: ['jump', 'run'],
  flip: ['jump', 'run'],
  fall: ['fall', 'jump', 'run'],
  hit: ['hit', 'idle', 'run'],
  win: ['win', 'idle', 'run'],
  lose: ['lose', 'idle', 'run'],
  cheer: ['win', 'idle', 'run'],
  slump: ['lose', 'hit', 'idle', 'run'],
  sit: ['idle', 'run'],
  burrow: ['jump', 'run'],
}

export function poseImages(anim: RunnerAnim): readonly string[] {
  return FALLBACKS[anim]
}

/** Vertical speed (px/s) under which a jump strip shows its apex frame. */
const APEX_VY = 150

/** Jump strip frames follow the arc, not the clock: rising, apex, falling. */
export function jumpFrame(vy: number, frames: number): number {
  if (frames < 3) return vy < 0 ? 0 : frames - 1
  if (vy < -APEX_VY) return 0
  if (vy > APEX_VY) return frames - 1
  return Math.floor(frames / 2)
}

/**
 * Code animation of a single pose: scale factors, a vertical offset (world px, negative = up),
 * a rotation (radians, clockwise) and the point it turns and squashes around.
 */
export interface PoseMotion {
  sx: number
  sy: number
  dy: number
  rot: number
  pivot: 'feet' | 'center'
}

export const STILL: PoseMotion = { sx: 1, sy: 1, dy: 0, rot: 0, pivot: 'feet' }

const TAU = Math.PI * 2
/** One running step (a foot lands every half period). */
const STEP_MS = 320
const BREATH_MS = 1400
const HOP_MS = 520
/** The podium cheer bounces once per this long (PHASE3 T3.5). */
export const CHEER_MS = 1200

/** Motion `t` ms into pose `anim`. */
export function poseMotion(anim: RunnerAnim, t: number): PoseMotion {
  switch (anim) {
    case 'run': {
      const up = Math.abs(Math.sin((t / STEP_MS) * TAU))
      const squash = 0.05 * (1 - 2 * up)
      return {
        sx: 1 - squash,
        sy: 1 + squash,
        dy: -3 * up,
        rot: 0.08 + 0.03 * Math.sin((t / STEP_MS) * TAU),
        pivot: 'feet',
      }
    }
    case 'jump': {
      const k = Math.max(0, 1 - t / 250)
      return { sx: 1 - 0.08 * k, sy: 1.03 + 0.12 * k, dy: 0, rot: 0, pivot: 'feet' }
    }
    case 'flip':
      return { sx: 1, sy: 1, dy: 0, rot: (TAU * Math.min(t, FLIP_MS)) / FLIP_MS, pivot: 'center' }
    case 'fall':
      return { sx: 0.97, sy: 1.04, dy: 0, rot: 0, pivot: 'feet' }
    case 'hit':
      // The renderer turns the whole runner head over heels (D-045).
      return STILL
    case 'win': {
      const s = Math.sin((t / HOP_MS) * TAU)
      const land = Math.max(0, -s)
      return {
        sx: 1 + 0.08 * land,
        sy: 1 - 0.08 * land,
        dy: -10 * Math.max(0, s),
        rot: 0,
        pivot: 'feet',
      }
    }
    case 'lose': {
      const b = Math.sin((t / (BREATH_MS * 1.5)) * TAU)
      return { sx: 1.03, sy: 0.95 + 0.01 * b, dy: 0, rot: 0, pivot: 'feet' }
    }
    case 'idle': {
      const b = Math.sin((t / BREATH_MS) * TAU)
      return { sx: 1 - 0.015 * b, sy: 1 + 0.03 * b, dy: 0, rot: 0, pivot: 'feet' }
    }
    case 'cheer': {
      const s = Math.max(0, Math.sin((t / CHEER_MS) * TAU))
      return { sx: 1, sy: 1, dy: -12 * s, rot: 0, pivot: 'feet' }
    }
    case 'slump':
      return { sx: 1.08, sy: 0.88, dy: 0, rot: 0, pivot: 'feet' }
    case 'sit':
      return { sx: 1, sy: 0.9, dy: 0, rot: 0, pivot: 'feet' }
    case 'burrow':
      return { sx: 1.05, sy: 0.9, dy: 0, rot: 0.3, pivot: 'feet' }
  }
}
